import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import Select from "react-select";
import { validateRecord } from "helpers/estimatingDatabase/databaseRows";
import { validateFormula } from "helpers/estimatingDatabase/jaceFormula";
import { FormulaEditor } from "./FormulaEditor";
import { toPercent, fromPercent } from "helpers/estimatingDatabase/displayFormats";

// What the input shows for a stored value, and back. Money is edited in the
// version's currency; a field may add its own conversion (yields).
export const toInput = (f, v, rate) => {
  if (f.toInput) return f.toInput(v);
  // a percentage is typed 0-100 whatever its stored form (PercentageConverter)
  if (f.percent) return toPercent(Number(v) || 0, f.percent);
  if (f.type === "number") {
    const n = Number(v) || 0;
    return f.currency ? Math.round(n * rate * 100) / 100 : n;
  }
  return v === undefined || v === null ? (f.type === "multiselect" ? [] : "") : v;
};

export const fromInput = (f, v, rate) => {
  if (f.fromInput) return f.fromInput(v);
  if (f.percent) return v === "" || v === null || v === undefined ? 0 : fromPercent(v, f.percent);
  if (f.type === "number") {
    if (v === "" || v === null || v === undefined) return 0;
    const n = Number(v);
    return f.currency ? n / (rate || 1) : n;
  }
  return v;
};

// BaseDatabaseModel.EditItem copies back only what the user changed: the
// input may show a rounded value (money to the cent, yields to 4 places),
// but a field nobody typed into keeps its stored value to the last digit.
export function applyForm(initial, fields, form, touched, rate = 1) {
  const rec = { ...(initial || {}) };
  fields.forEach((f) => {
    if (f.type === "readonly") return;
    if (initial && !(touched && touched[f.key]) && initial[f.key] !== undefined) return;
    rec[f.key] = fromInput(f, form[f.key], rate);
  });
  return rec;
}

// Add / edit form for one record, built from the page's field list.
export const RecordModal = ({
  open,
  title,
  fields,
  initial,
  rate = 1,
  options = () => [],
  others = [],
  onCancel,
  onSubmit,
  extraValidate,
}) => {
  const [form, setForm] = useState({});
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const next = {};
    fields.forEach((f) => {
      next[f.key] = toInput(f, initial ? initial[f.key] : undefined, rate);
    });
    setForm(next);
    setTouched({});
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const submit = () => {
    const rec = applyForm(initial, fields, form, touched, rate);
    const errs = validateRecord(rec, fields.filter((f) => f.type !== "readonly"), others);
    fields.forEach((f) => {
      if (f.type === "formula" && rec[f.key] && !errs[f.key]) {
        const check = validateFormula(rec[f.key]);
        if (!check.valid) errs[f.key] = check.error;
      }
    });
    Object.assign(errs, extraValidate ? extraValidate(rec) || {} : {});
    setErrors(errs);
    if (Object.keys(errs).length) return;
    onSubmit(rec);
  };

  const set = (key, value) => {
    setForm((cur) => ({ ...cur, [key]: value }));
    setTouched((cur) => ({ ...cur, [key]: true }));
  };

  const renderInput = (f) => {
    const v = form[f.key];
    if (f.type === "readonly") {
      return <div className="form-control-plaintext">{v === "" || v === undefined ? "--" : String(v)}</div>;
    }
    if (f.type === "formula") {
      return <FormulaEditor value={v} onChange={(val) => set(f.key, val)} />;
    }
    if (f.type === "textarea") {
      return (
        <textarea className="form-control" rows={3} value={v || ""} onChange={(e) => set(f.key, e.target.value)} />
      );
    }
    if (f.type === "select" || f.type === "multiselect") {
      const opts = options(f) || [];
      const multi = f.type === "multiselect";
      // A stored value the list does not hold stays selected (shown as is)
      // so saving the dialog never drops it.
      const value = multi
        ? (v || []).map((x) => opts.find((o) => o.value === x) || { value: x, label: String(x) })
        : opts.find((o) => o.value === v) || (v ? { value: v, label: String(v) } : null);
      return (
        <Select
          classNamePrefix={`database-${f.key}`}
          options={opts}
          value={value}
          isMulti={multi}
          isClearable={!f.required}
          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
          onChange={(sel) =>
            set(f.key, multi ? (sel || []).map((s) => s.value) : sel ? sel.value : "")
          }
        />
      );
    }
    return (
      <input
        type={f.type === "number" ? "number" : "text"}
        min={f.percent ? 0 : undefined}
        max={f.percent ? 100 : undefined}
        step="any"
        className="form-control"
        aria-label={f.label}
        value={v === undefined || v === null ? "" : v}
        onChange={(e) => set(f.key, e.target.value)}
      />
    );
  };

  return (
    // The desktop's dialog (DialogHost/*InputTemplate.xaml): a narrow form, the
    // labels right-aligned in their own column, CANCEL and OK underneath.
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      onOk={submit}
      okText="OK"
      cancelText="CANCEL"
      destroyOnClose
      width={560}
      className="db-dialog"
    >
      <div className="db-dialog-grid" data-testid="record-modal">
        {fields.map((f) => (
          <div key={f.key} className="db-dialog-row">
            <label className="db-dialog-label" title={f.currency ? "In the version's currency" : undefined}>
              {f.label}
              {f.required ? " *" : ""}
            </label>
            <div className="db-dialog-field">
            {renderInput(f)}
            {errors[f.key] && f.type !== "formula" && (
              <div className="text-danger f-12 mt-1">{errors[f.key]}</div>
            )}
            {errors[f.key] && f.type === "formula" && form[f.key] === "" && (
              <div className="text-danger f-12 mt-1">{errors[f.key]}</div>
            )}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};
